import { type ReactElement, useState } from 'react';
import { useNavigate } from 'react-router';
import type { useMyOrderDetailQuery } from '@/entities/order';
import type { useBankTransferQuery } from '@/entities/payment';
import {
  BankTransferEditDialog,
  bankTransferApiErrorMessage,
  type useAbandonBankTransfer,
} from '@/features/bank-transfer';
import { OrderUnpaidCancelDialog, type UseCancelOrderResult } from '@/features/order-cancel';
import { myOrderDetailPath, paymentCheckoutPath } from '@/shared/config';
import { isOrderConflict, orderMutationMessage } from '@/shared/lib';
import { Button } from '@/shared/ui';
import { OrderDepositGuide } from './order-deposit-guide';

interface OrderDepositProps {
  /** 주소에서 해석한 정규화 주문 id. */
  readonly orderId: string;
  readonly transfer: ReturnType<typeof useBankTransferQuery>;
  readonly order: ReturnType<typeof useMyOrderDetailQuery>;
  readonly abandon: ReturnType<typeof useAbandonBankTransfer>;
  readonly cancel: UseCancelOrderResult;
}

/**
 * 계좌이체 입금 안내(계좌이체·전자세금계산서 설계 §6) — 결제·주문 상태로 안내 가능 여부를 정하고, 결제 방법 바꾸기·주문 취소·
 * 입금 정보 수정 대화상자를 다룬다. 결제 READY 행은 주문 취소 뒤에도 남을 수 있어 주문 상태까지 함께 확인한다.
 */
export const OrderDeposit = ({
  orderId,
  transfer,
  order,
  abandon,
  cancel,
}: OrderDepositProps): ReactElement => {
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [canceling, setCanceling] = useState(false);
  const [methodFailure, setMethodFailure] = useState<string | null>(null);
  const [cancelFailure, setCancelFailure] = useState<string | null>(null);
  const canDeposit =
    transfer.isSuccess &&
    order.isSuccess &&
    transfer.data.status === 'READY' &&
    order.data.status === 'PENDING_PAYMENT';
  const reload = () => {
    void transfer.refetch();
    void order.refetch();
  };

  const changeMethod = async () => {
    if (!canDeposit) return;
    setMethodFailure(null);
    try {
      await abandon.abandon({ orderId });
      void navigate(paymentCheckoutPath(orderId));
    } catch (error) {
      setMethodFailure(bankTransferApiErrorMessage(error));
    }
  };
  const cancelOrder = async () => {
    if (!canDeposit) return;
    setCancelFailure(null);
    try {
      await cancel.cancel({ orderId });
      setCanceling(false);
      void navigate(myOrderDetailPath(orderId));
    } catch (error) {
      setCancelFailure(orderMutationMessage(error));
      if (isOrderConflict(error)) reload();
    }
  };

  if (transfer.isPending || order.isPending)
    return <p role="status">입금 안내를 불러오는 중입니다.</p>;
  if (transfer.isError || order.isError)
    return (
      <div role="alert">
        <p>
          {transfer.isError
            ? bankTransferApiErrorMessage(transfer.error)
            : '주문 정보를 불러오지 못했습니다.'}
        </p>
        <Button onClick={reload}>다시 시도</Button>
      </div>
    );

  return (
    <>
      {order.data.status === 'CANCELED' ? (
        <p role="status">취소된 주문입니다. 입금하지 마세요.</p>
      ) : order.data.status === 'REFUNDED' ? (
        <p role="status">환불이 완료된 주문입니다.</p>
      ) : transfer.data.status === 'READY' && !canDeposit ? (
        <p role="status">이미 결제가 완료된 주문입니다.</p>
      ) : (
        <OrderDepositGuide
          orderId={order.data.orderNumber}
          transfer={transfer.data}
          taxInvoiceRequested={transfer.data.taxInvoiceRequest !== null}
          onEdit={() => setEditing(true)}
          onChangeMethod={() => {
            void changeMethod();
          }}
          onCancel={() => {
            setCancelFailure(null);
            setCanceling(true);
          }}
          pending={abandon.isPending || cancel.isPending}
        />
      )}
      {methodFailure ? <p role="alert">{methodFailure}</p> : null}
      {canceling && order.data.status === 'PENDING_PAYMENT' ? (
        <OrderUnpaidCancelDialog
          onConfirm={() => {
            void cancelOrder();
          }}
          onClose={() => setCanceling(false)}
          pending={cancel.isPending}
          confirmable={canDeposit}
          message={cancelFailure}
        />
      ) : null}
      {editing && canDeposit ? (
        <BankTransferEditDialog
          orderId={orderId}
          transfer={transfer.data}
          onClose={() => setEditing(false)}
        />
      ) : null}
    </>
  );
};
